import { useCallback, useMemo, useRef } from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { FULL_CONNECTOR_SOURCE } from '../../common/types/schemas/types';
import { buildHelpAggregatorConnector } from './helpers/buildHelpAggregatorConnector';
import { coerceFirstPartyFriendlyName } from './helpers/coerceFirstPartyFriendlyName';
import { coerceFriendlyName } from './helpers/coerceFriendlyName';
import { getIconUrlForSource, resolveIconUrlForSource } from './helpers/getIconUrlForSource';
import { isRecognisedFirstPartyConnector } from './helpers/isRecognisedFirstPartyConnector';
import { isSupportedThirdPartyConnector } from './helpers/isSupportedThirdPartyConnector';
import { resolveOAuthTargetForSource } from './helpers/resolveOAuthTargetForSource';
import { sortConnectorsByFriendlyName } from './helpers/sortConnectorsByFriendlyName';
import { resolveChatSource, toChatSourceKey } from './helpers/toChatSourceKey';
import { useRovoConfig } from './useRovoConfig';

/**
 * @file This is a thin adapter designed to allow @atlassian/conversation-assistant-store (Chat, Agents, etc) to consume Rovo connectors from @atlassian/rovo-connectors-config with minimal changes to the existing chat selector hooks. Uses {@link useRovoConfig} under the hood to fetch the actual config, and projects it into the shape expected by the chat selector hooks.
 */

/**
 * Minimum incoming API required by {@link useRovoConfigStore}.
 *
 * RAOC-23: this is the incoming API contract for the chat selector hooks' migration
 *
 * Deliberately the **narrowest possible** subset of `UseRovoConfigProps`.
 * The hook's whole purpose is to be a drop-in replacement for the
 * zero-arg chat selector hooks in
 * `@atlassian/conversation-assistant-store/controllers/connectors-store`
 */
export type UseRovoConfigStoreProps = {
	/**
	 * TODO RAOC-23: change `cloudId` to a required field once chat
	 * selector hooks always have a tenant in hand. While it's optional,
	 * callers that omit it get a permanently-idle store (see the
	 * `cloudId`-absent branch in `useRovoConfigStore`) — which is safer
	 * than spinning forever, but masks bugs at the call site.
	 */
	cloudId?: string;
};

/**
 * Canonical "chat-shape" knowledge configuration for a single connector.
 *
 * Re-declared inside `rovo-connectors-config` rather than imported from
 * `@atlassian/conversation-assistant-service-api` so that this package does
 * not take a runtime/type dependency on the chat package.
 *
 * Drift between this re-declaration and the chat-side declarations is guarded
 * at typecheck time by the downstream test
 * `conversation-assistant-store/src/controllers/connectors-store/connectors-store-types-drift.test.tsx`
 * (RAOC-23) — it lives there because only that package may import both copies.
 *
 * Once this hook is the canonical implementation, `conversation-assistant-store` will become a thin
 * re-export façade over `@atlassian/rovo-connectors-config`.
 *
 * Mirrors `AgentKnowledgeConfiguration` from
 * `@atlassian/conversation-assistant-service-api/types/assistance-service`.
 * Any structural drift between the two should be treated as a bug.
 */
export type AgentKnowledgeConfiguration = {
	/**
	 * TODO RAOC-23: use 3P keys for typing here, drop `| string`
	 * TODO RAOC-23: where possible, move to enum types (eg `connectorSyncType`) here rather than string
	 * Known sources; allow `string` for forward-compat with new 1P / 3P sources.
	 **/
	source:
		| 'confluence'
		| 'jira'
		| 'jsm'
		| 'atlas'
		| 'microsoft_sharepoint'
		| 'google_drive'
		| 'slack'
		| 'loom'
		| string;
	iconUrl: string;
	friendlyName: string;
	isUserOAuthed: boolean;
	authResolutionMode?: string;
	outboundAuthUrl: string;
	connectorCreatedAt: string | null;
	connectedAt: string | null;
	resourceType: string | null;
	providerId: string | null;
	datasourceId: string | null;
	connectorSyncType: string | null;
	/** Preserve all resolved source categories, including mixed Full/Smart Link configurations. */
	connectorSources?: readonly string[];
};

/**
 * Per-connector state used inside {@link RovoConfigStoreState}.
 *
 * Mirrors the `ConnectorState` declared by
 * `@atlassian/conversation-assistant-store/controllers/connectors-store`:
 * the full {@link AgentKnowledgeConfiguration}, plus a client-only
 * `lastUsedAt` epoch-ms timestamp that the conversation-assistant-store
 * backs with localStorage.
 *
 * TODO RAOC-23: support this with local state and persistence in this package rather than leaving it to the chat package, since it's not really chat-specific and we want to avoid leaving part of the shape unpopulated until the chat selector hooks are fully migrated.
 */
export type RovoConnectorState = AgentKnowledgeConfiguration & {
	/** Epoch ms when the user last interacted with this app (selected in filter or mentioned in chat). */
	lastUsedAt?: number;
};

/**
 * Canonical store-state shape returned by {@link useRovoConfigStore}.
 *
 * 1:1 mirror of `ConnectorsStoreState` from
 * `@atlassian/conversation-assistant-store/controllers/connectors-store`.
 * This is the value each of the existing chat selector hooks
 * (`useEnabledKnowledgeSources`, `useConnectorsIsFetching`,
 * `useConnectorsDataReady`, `useKnowledgeSourcesStore`, …) currently reads
 * out of its react-sweet-state store; under the migration FG those hooks
 * will swap their store reads for a selector over the value returned by
 * `useRovoConfigStore()`.
 *
 * Drift between this 1:1 mirror and the chat-side `ConnectorsStoreState` is
 * guarded at typecheck time by the downstream test
 * `conversation-assistant-store/src/controllers/connectors-store/connectors-store-types-drift.test.tsx`
 * (RAOC-23).
 */
export type RovoConfigStoreState = {
	/** Map of connector source to its full state. */
	connectors: Record<string, RovoConnectorState>;
	isFetching: boolean;
	error: Error | null;
	lastFetchedAt: number | null;
	/** True after the first fetch attempt (success or failure). Prevents automatic retries on unrecoverable errors. */
	hasFetchAttempted: boolean;
};

/**
 * Full return value of {@link useRovoConfigStore}: the {@link RovoConfigStoreState}
 * read-state, plus the connector write-API.
 *
 * Kept distinct from {@link RovoConfigStoreState} so the latter remains a pure
 * 1:1 *state* mirror of the chat-side `ConnectorsStoreState` (guarded by the
 * `connectors-store-types-drift.test.tsx` typecheck). Actions are layered on
 * here, mirroring how the chat side keeps `ConnectorsStoreState` and its bound
 * actions separate.
 */
export type RovoConfigStore = RovoConfigStoreState & {
	/**
	 * Mark a connector's user as freshly OAuth-connected.
	 *
	 * Invoked with the connector's chat-namespace `source` key (e.g. `'slack'`,
	 * `'google_drive'`). Internally translates the `source` to its
	 * config-namespace product / datasource (see
	 * {@link resolveOAuthTargetForSource}) and calls `useRovoConfig`'s
	 * `onOAuthSuccess`, clearing the connector's `userNeedsOAuth` flag so the
	 * read-state reflects the connection on the next render.
	 *
	 * Unknown / 1P / synthetic / unconfigured sources are no-ops, mirroring
	 * `onOAuthSuccess`'s own no-op-on-unknown contract. When `cloudId` is absent
	 * the store is idle and this is a no-op (there is no config to mutate).
	 *
	 * All instances of `useRovoConfig` (and therefore `useRovoConfigStore`) are
	 * subscribed to the same singleton store of OAuth-connected products/datasources,
	 * so a single call to `markUserConnected` will propagate the connection state to all instances.
	 */
	markUserConnected: (source: string) => void;
};

/**
 * @deprecated use useRovoConfig directly instead of this hook, this is a thin adapter designed to support \@atlassian/conversation-assistant-store only.
 *
 * Minimum incoming API required by {@link useRovoConfigStore}.
 *
 * Provides Rovo connectors to downstreams of \@atlassian/conversation-assistant-store
 * as part of RAOC-23 where Chat/Agents use the same config path as Search
 * such that the UX is always in sync
 *
 * Deliberately the **narrowest possible** subset of {@link UseRovoConfigProps}.
 * The hook's whole purpose is to power a drop-in replacement for the
 * zero-arg chat selector hooks in
 * `@atlassian/conversation-assistant-store/controllers/connectors-store`,
 * so every prop we expose here becomes one more thing each chat call
 * site has to wire up. Anything that can be sourced internally is.
 *
 * @see {@link UseRovoConfigStoreProps} for the incoming API contract.
 * @see {@link RovoConfigStoreState} for the output shape.
 */
export const useRovoConfigStore = (props: UseRovoConfigStoreProps = {}): RovoConfigStore => {
	const { accessibleProducts, thirdPartyConfigs, isConfigLoaded, onOAuthSuccess } = useRovoConfig({
		cloudId: props.cloudId,
		// Intentionally not surfaced on UseRovoConfigStoreProps — see the
		// type-level comment on that interface for rationale.
		aggAbsoluteUrl: undefined,
		multiSiteSearchInRovo: undefined,
		preloadDataProvider: undefined,
		shouldBypassPreloadProvider: undefined,
		onForgeConnectorTypeMapping: undefined,
	});

	// When `cloudId` is absent `useRovoConfig` short-circuits its fetch
	// effect, so `isConfigLoaded` would stay `false` forever and chat
	// consumers would spin indefinitely with no indication that no
	// request was ever in flight. Surface an "idle" store instead:
	// not fetching, not yet attempted, empty connectors.
	// TODO RAOC-23: tighten this — `cloudId` should be a *required* prop
	// on `UseRovoConfigStoreProps` once every chat call site can supply
	// it. Removing this branch will let TypeScript catch missing tenants
	// at the call site rather than papering over them at runtime.
	const isIdle = !props.cloudId;

	// Keep a live ref to the latest `thirdPartyConfigs` so `markUserConnected`
	// resolves the `source` against the *current* config rather than the
	// render-snapshot it closed over. `useRovoConfig`'s `onOAuthSuccess` mutates
	// via its own internal ref synchronously, so reading the snapshot here would
	// resolve against stale config if `markUserConnected` is called again before
	// the resulting re-render. Mirroring the ref keeps resolution and mutation
	// consistent.
	const thirdPartyConfigsRef = useRef(thirdPartyConfigs);
	thirdPartyConfigsRef.current = thirdPartyConfigs;

	// `markUserConnected(source)` — part of the store's public API (the OAuth
	// write counterpart to the connectors read-state above). The actual
	// chat-`source` → config-namespace product/datasource translation lives in
	// {@link resolveOAuthTargetForSource} to keep this hook a thin wiring layer.
	// No-op while idle, and no-op for sources with no matching 3P config.
	const markUserConnected = useCallback(
		(source: string): void => {
			if (isIdle) {
				return;
			}
			const target = resolveOAuthTargetForSource(thirdPartyConfigsRef.current, source);
			if (!target) {
				return;
			}
			onOAuthSuccess(target);
		},
		[isIdle, onOAuthSuccess],
	);

	return useMemo<RovoConfigStore>(() => {
		if (isIdle) {
			return {
				connectors: {},
				isFetching: false,
				error: null,
				lastFetchedAt: null,
				hasFetchAttempted: false,
				markUserConnected,
			};
		}

		// Connectors are accumulated here, then sorted by friendlyName when
		// building the final record (see `sortConnectorsByFriendlyName`).
		const connectorEntries: RovoConnectorState[] = [];
		// Source keys already produced, so 1P synthesis doesn't duplicate a
		// 3P connector.
		const seenKeys = new Set<string>();
		// 3P keys with a null config — skip them in 1P synthesis below so they
		// aren't misclassified as authed 1P products.
		const skipped3pKeys = new Set<string>();

		// 3P (and Forge) connectors — rich metadata from the config endpoint.
		// TODO: here we will need to async import/bind the 3P configs into the list from the endpoint (powers url, names, etc)
		if (thirdPartyConfigs) {
			for (const [productKey, config] of Object.entries(thirdPartyConfigs)) {
				// Some 3P connectors are returned by the config endpoint but are
				// not yet supported on the chat surface (e.g. `jsm-dc`), so skip
				// anything we do not yet support. This is the 3P counterpart to
				// the 1P allow-list guard below. See
				// {@link isSupportedThirdPartyConnector}.
				//
				// Mark the raw product key's chat form as handled so that, if the
				// unsupported key also appears in `accessibleProducts`, the 1P
				// loop below does not synthesise a phantom authed 1P entry for it
				// — the same reason the null-config and post-projection guards
				// populate `skipped3pKeys`.
				if (!isSupportedThirdPartyConnector(productKey)) {
					skipped3pKeys.add(toChatSourceKey(productKey));
					continue;
				}
				// Remap the config-namespace identifier to the chat-namespace
				// `source` so downstream chat selectors find the connector
				// regardless of the `rovo_cas_use_rovo_config` gate. 3P is keyed
				// on the unambiguous `resourceType` (connector key); when the
				// config is null we have no resourceType, so fall back to the
				// product-key remap. See {@link resolveChatSource}.
				const sourceKey = resolveChatSource(productKey, config?.resourceType);
				if (!config) {
					skipped3pKeys.add(sourceKey);
					continue;
				}
				seenKeys.add(sourceKey);
				const hasAdminImportedUserLink = Boolean(
					config.connectorSources?.includes(FULL_CONNECTOR_SOURCE) &&
					config.datasources?.some(
						(datasource) => datasource.authResolutionMode === 'ADMIN_IMPORTED_USER_LINK',
					) &&
					fg('ai_3p_cds_auth_resolution_mode_flock'),
				);
				connectorEntries.push({
					source: sourceKey,
					iconUrl: !config.iconUrl
						? getIconUrlForSource(sourceKey)
						: fg('rovo_cas_validate_connector_icon_url')
							? resolveIconUrlForSource(sourceKey, config.iconUrl)
							: config.iconUrl,
					friendlyName: coerceFriendlyName(config.displayName, config.workspaceName, productKey),
					// `userNeedsOAuth === false` => user has authed; treat unknown as authed
					// to avoid hiding connectors that don't participate in OAuth.
					isUserOAuthed: config.userNeedsOAuth !== true,
					authResolutionMode: hasAdminImportedUserLink ? 'ADMIN_IMPORTED_USER_LINK' : undefined,
					outboundAuthUrl: config.outboundAuthUrl ?? '',
					connectorCreatedAt: config.connectorCreatedAt ?? null,
					connectedAt: config.connectedAt ?? null,
					resourceType: config.resourceType ?? null,
					providerId: config.providerId ?? null,
					datasourceId: config.datasourceId ?? null,
					connectorSyncType: config.connectorSyncType ?? null,
					connectorSources: config.connectorSources,
				});
				// The 3P entry was written under the (possibly remapped) chat
				// `sourceKey`, but `accessibleProducts` references the raw product
				// key. Mark the raw product key's chat form as handled so the 1P
				// loop below does not synthesise a phantom authed 1P entry under
				// the original product key alongside the correctly-remapped 3P
				// entry (e.g. a remapped `drive` → `google_drive` would otherwise
				// also produce a stray `drive` 1P entry).
				skipped3pKeys.add(toChatSourceKey(productKey));
			}
		}

		// 1P products — synthesised stub entries so the shape is complete.
		// `accessibleProducts` is the union of 1P + already-OAuthed 3P, so we
		// skip (a) any key already covered by `thirdPartyConfigs` above, and
		// (b) any key we *intentionally* skipped from `thirdPartyConfigs`
		// because its config was null — those are still 3P, just unconfigured.
		if (accessibleProducts) {
			for (const productKey of accessibleProducts) {
				// `accessibleProducts` includes combined-only sub-product keys
				// (e.g. `jira-core`, `jira-software`) and accessible products
				// that are not chat connectors (e.g. `avp`, `devai`). The FG-off
				// knowledge endpoint never surfaced those as connectors, and they
				// have no friendly name or resolvable icon, so skip anything we do
				// not explicitly recognise. See {@link isRecognisedFirstPartyConnector}.
				if (!isRecognisedFirstPartyConnector(productKey)) {
					continue;
				}
				// 1P / noun products have no `resourceType`, so they are remapped
				// by product key. Done before the collision checks so a remapped
				// key (e.g. `jira-servicedesk` → `jsm`) is compared against the
				// same namespace used for the 3P entries and `skipped3pKeys`
				// above. See {@link toChatSourceKey}.
				const sourceKey = toChatSourceKey(productKey);
				if (seenKeys.has(sourceKey) || skipped3pKeys.has(sourceKey)) {
					continue;
				}
				seenKeys.add(sourceKey);
				connectorEntries.push({
					source: sourceKey,
					iconUrl: getIconUrlForSource(sourceKey),
					friendlyName: coerceFirstPartyFriendlyName(productKey),
					isUserOAuthed: true, // 1P is implicitly authed.
					outboundAuthUrl: '',
					connectorCreatedAt: null,
					connectedAt: null,
					resourceType: null,
					providerId: null,
					datasourceId: null,
					connectorSyncType: null,
				});
			}
		}

		// Always-present synthetic connector for the help/support docs
		// aggregator, added once config has loaded. It is authoritative for its
		// key: any real connector already produced with the same `source` is
		// replaced rather than duplicated.
		if (isConfigLoaded) {
			const helpAggregator = buildHelpAggregatorConnector();
			if (seenKeys.has(helpAggregator.source)) {
				const existingIndex = connectorEntries.findIndex(
					(entry) => entry.source === helpAggregator.source,
				);
				connectorEntries.splice(existingIndex, 1);
			}
			connectorEntries.push(helpAggregator);
		}

		return {
			connectors: sortConnectorsByFriendlyName(connectorEntries),
			isFetching: !isConfigLoaded,
			error: null,
			lastFetchedAt: null,
			hasFetchAttempted: isConfigLoaded,
			markUserConnected,
		};
	}, [isIdle, accessibleProducts, thirdPartyConfigs, isConfigLoaded, markUserConnected]);
};
